import { useState, useEffect } from "react";
import { Row, Col, Form, Input, Switch, Button, message } from "antd";
import { useSelector } from "react-redux";
import { Post, Get } from "../../config/api";

const DiscountManagement = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [form] = Form.useForm();

  const token = useSelector((state) => state.user.userToken);

  // Load current pricing settings on mount
  useEffect(() => {
    const loadCurrentSettings = async () => {
      try {
        setInitialLoading(true);
        const response = await Get("/pricing/getPricing", token);
        if (response?.data) {
          form.setFieldsValue({
            amount: response.data.advanceBookingDiscountPercent || 0,
            enabled: response.data.advanceBookingDiscountEnabled || false,
            bookingFee: response.data.bookingFee || 0,
            serviceFee: response.data.serviceFee || 0,
            gratuityPercent: response.data.gratuityPercent || 0,
          });
        }
      } catch (err) {
        console.error("Failed to load current settings:", err);
        message.error("Failed to load current pricing settings.");
      } finally {
        setInitialLoading(false);
      }
    };

    if (token) {
      loadCurrentSettings();
    }
  }, [token, form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const payload = {};
      
      // Only include changed values
      if (values.amount !== undefined && values.amount !== null && values.amount !== "") {
        const numericAmount = Number(values.amount);
        if (!isNaN(numericAmount)) {
          payload.advanceBookingDiscountPercent = numericAmount;
        }
      }
      if (typeof values.enabled === "boolean") {
        payload.advanceBookingDiscountEnabled = values.enabled;
      }
      if (values.bookingFee !== undefined && values.bookingFee !== null && values.bookingFee !== "") {
        const numericBookingFee = Number(values.bookingFee);
        if (!isNaN(numericBookingFee)) {
          payload.bookingFee = numericBookingFee;
        }
      }
      if (values.serviceFee !== undefined && values.serviceFee !== null && values.serviceFee !== "") {
        const numericServiceFee = Number(values.serviceFee);
        if (!isNaN(numericServiceFee)) {
          payload.serviceFee = numericServiceFee;
        }
      }
      if (values.gratuityPercent !== undefined && values.gratuityPercent !== null && values.gratuityPercent !== "") {
        const numericGratuity = Number(values.gratuityPercent);
        if (!isNaN(numericGratuity)) {
          payload.gratuityPercent = numericGratuity;
        }
      }

      const response = await Post("/pricing/setPricing", payload, token);
      if (response?.status === true || response?.success === true) {
        message.success("Pricing settings saved.");
      } else {
        message.error("Failed to save pricing settings.");
      }
    } catch (err) {
      message.error("Failed to save pricing settings.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row justify="center">
      <Col md={23} xs={22}>
        <div
          className="bg-white rounded-lg sm:py-16 py-4 my-8"
          style={{ boxShadow: "1px 1px 1px 1px rgba(0, 0, 0, 0.05)" }}
        >
          <div className="flex justify-between items-center mx-4 lg:mx-8">
            <div className="flex justify-center items-center">
              <h3 className="font-tavi lg:text-3xl text-2xl max-sm:text-lg font-normal text-black">
                Pricing Management
              </h3>
            </div>
          </div>

          <Row className="mt-8" justify="left">
            <Col xs={22} sm={18} md={16} lg={12} xxl={10}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ amount: 0, enabled: false, bookingFee: 0, serviceFee: 0, gratuityPercent: 0 }}
                loading={initialLoading}
                className="w-full px-8 max-xl:px-2 customSelect1"
              >
                <Row gutter={[16, 0]} justify="left">
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<p className="font-euclid text-sm text-[#34324B]">Discount Amount (%)</p>}
                      name="amount"
                      rules={[
                        { required: true, message: "Please enter discount amount" },
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "") return Promise.resolve();
                            const n = Number(value);
                            if (isNaN(n) || n < 0 || n > 100)
                              return Promise.reject("Enter a value between 0 and 100");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter Discount Percent"
                        type="number"
                        className="h-12 text-sm font-euclid bg-gray-100 max-sm:text-xs"
                        onWheel={(e) => e.target.blur()}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<p className="font-euclid text-sm text-[#34324B]">Enable Discount</p>}
                      name="enabled"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]} justify="left" style={{ marginTop: "16px" }}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<p className="font-euclid text-sm text-[#34324B]">Booking Fee ($)</p>}
                      name="bookingFee"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "") return Promise.resolve();
                            const n = Number(value);
                            if (isNaN(n) || n < 0)
                              return Promise.reject("Enter a value greater than or equal to 0");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter Booking Fee"
                        type="number"
                        step="0.01"
                        className="h-12 text-sm font-euclid bg-gray-100 max-sm:text-xs"
                        onWheel={(e) => e.target.blur()}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<p className="font-euclid text-sm text-[#34324B]">Service Fee ($)</p>}
                      name="serviceFee"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "") return Promise.resolve();
                            const n = Number(value);
                            if (isNaN(n) || n < 0)
                              return Promise.reject("Enter a value greater than or equal to 0");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter Service Fee"
                        type="number"
                        step="0.01"
                        className="h-12 text-sm font-euclid bg-gray-100 max-sm:text-xs"
                        onWheel={(e) => e.target.blur()}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]} justify="left" style={{ marginTop: "16px" }}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<p className="font-euclid text-sm text-[#34324B]">Gratuity Percentage (%)</p>}
                      name="gratuityPercent"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === "") return Promise.resolve();
                            const n = Number(value);
                            if (isNaN(n) || n < 0 || n > 100)
                              return Promise.reject("Enter a value between 0 and 100");
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter Gratuity Percent"
                        type="number"
                        className="h-12 text-sm font-euclid bg-gray-100 max-sm:text-xs"
                        onWheel={(e) => e.target.blur()}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row justify="left">
                  <Col lg={12}>
                    <Form.Item className="self-center text-left">
                      <Button
                        htmlType="submit"
                        loading={loading}
                        className="uppercase bg-button hover:bg-white hover:border-customGold hover:border-2 font-euclid font-bold text-white px-14 h-[50px] hover:text-customGold text-center text-sm new-hover"
                      >
                        {loading ? "LOADING..." : "save now"}
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  );
};

export default DiscountManagement;


